import { useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from "react";
import TableHelper from "./TableHelper";
import { TOOLBAR_MODE } from "@acadia-ui/toolbar";

const getErrorCellKeys = (errors) => new Set((errors || []).map(({ rowId, columnBindKey }) => `${rowId}-${columnBindKey}`));

export default function useTableDisabledCellStateApi({ ref, dataTableApi, config, hierarchyLevel, errors, dataProvider, columns, gcmStatus, metaData }) {
	const [cellState, setCellState] = useState({ default: {}, edit: {} });
	const errorsRef = useRef(errors);
	errorsRef.current = errors;

	const dataKeysByBind = useMemo(() => {
		const dataKeys = [
			...(config?.TaxonomyData || []),
			...(config?.MetaData || []),
			...(config?.ParentData || []),
			...(config?.[TableHelper.HIERARCHY_LEVEL_CONSTANTS[hierarchyLevel]?.DataBucketName] || [])
		];
		return dataKeys.reduce((acc, dataKey) => {
			acc[dataKey.Name] = dataKey;
			return acc;
		}, {});
	}, [config, hierarchyLevel]);

	const refreshDisabledCellStates = useCallback(
		(nextErrors = errorsRef.current) => {
			TableHelper.refreshDisabledCellStates({
				cellStateManager: dataTableApi?.current?.cellStateManager,
				dataProvider,
				columns,
				dataKeysByBind,
				hierarchyLevel,
				config,
				gcmStatus,
				metaData,
				errorCellKeys: getErrorCellKeys(nextErrors)
			});
		},
		[columns, config, dataKeysByBind, dataProvider, dataTableApi, gcmStatus, hierarchyLevel, metaData]
	);

	useLayoutEffect(() => {
		// Build a flattened, ordered list of only those dataKeys that will actually render as summarized header columns.
		// This allows us to deterministically assign isLastColumn based on the final eligible set rather than raw config indices.
		const eligibleSummarizedDataKeys = [];
		Object.keys(config).forEach((categoryName) => {
			if (TableHelper.NON_ARRAY_CONFIG_FIELDS.includes(categoryName)) return;
			config[categoryName].forEach((dataKey) => {
				if (!TableHelper.DESCRIPTION_FIELD_NAMES.includes(dataKey.Name) && dataKey.IsSummarizedHeader && dataKey.FieldType !== "Hidden") {
					eligibleSummarizedDataKeys.push(dataKey);
				}
			});
		});

		let newCellState = { ...cellState };

		eligibleSummarizedDataKeys.forEach((dataKey, idx) => {
			const isGCMCampaign = metaData?.PlatformID == 1;
			const isReadOnly = TableHelper.isReadOnlyEditCell({ bind: dataKey.Name, hierarchyLevel });
			const columnDisabledMap = TableHelper.getDisabledColumnMap(dataProvider, dataKey, isReadOnly, hierarchyLevel, config, metaData, gcmStatus, isGCMCampaign) || {};
			newCellState = { default: { ...newCellState.default, ...columnDisabledMap.default }, edit: { ...newCellState.edit, ...columnDisabledMap.edit } };
		});

		setCellState((prevState) => {
			const newState = { ...prevState, ...newCellState };
			return newState;
		});
	}, [config, metaData, dataProvider, hierarchyLevel, gcmStatus, dataTableApi]);

	useImperativeHandle(
		ref,
		() => ({
			get element() {
				return dataTableApi.current?.element;
			},
			get dataProvider() {
				return dataTableApi.current?.dataProvider;
			},
			get rowSelectionManager() {
				return dataTableApi.current?.rowSelectionManager;
			},
			get toolbarApi() {
				return dataTableApi.current?.toolbarApi;
			},
			get cellStateManager() {
				return dataTableApi.current?.cellStateManager;
			},
			get view() {
				return dataTableApi.current?.view;
			},
			focusCell: (...args) => dataTableApi.current?.focusCell?.(...args),
			refreshDisabledCellStates
		}),
		[dataTableApi, refreshDisabledCellStates]
	);

	return { cellState, refreshDisabledCellStates };
}
